import {
    useFinalizeAccountsMapping,
    useImportedAccounts,
    useAccountingMigration,
} from "@api/accountingIntegration.api";
import { Entity } from "@common/types/entity";
import { countImportedAccountMappingResolutions } from "@common/helpers/importedAccountMappingResolution";
import { MapImportedAccountsSpreadsheet } from "@components/accountingMigration/MapImportedAccountsSpreadsheet";
import { getImportedAccountProvider } from "@components/accountingMigration/importedAccountProvider";
import { ImportedAccountProviderHeader } from "@components/accountingMigration/ImportedAccountProviderHeader";
import { RefinalizeMappingConfirmationModal } from "@components/accountingMigration/RefinalizeMappingConfirmationModal";
import { useNavigateAfterMappingFinalized } from "@components/accountingMigration/useNavigateAfterMappingFinalized";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { SearchFilter } from "@design-system/SearchFilter/SearchFilter";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import styles from "./QuickbooksMapAccounts.module.scss";

interface Props {
    workspaceId: string;
    entity: Entity;
    nextTaskId?: string;
}

export const QuickbooksMapAccounts: React.FC<Props> = ({
    workspaceId,
    entity,
    nextTaskId,
}) => {
    const [isRefinalizeModalOpen, setIsRefinalizeModalOpen] = useState(false);

    const finalizeMutation = useFinalizeAccountsMapping(entity.id);

    const { data: importedAccountsData, refetch: refetchAccounts } =
        useImportedAccounts(workspaceId, entity.id, {
            orderBy: "name",
        });
    const accountsData = importedAccountsData?.accounts;
    const accountGroups = importedAccountsData?.accountGroups;

    const { data: accountingMigration } = useAccountingMigration(
        workspaceId,
        entity.id,
        { refetchUntilMappingFinished: true },
    );

    const provider = getImportedAccountProvider(
        accountingMigration?.externalSystem,
    );

    const isAccountsMappingRunning =
        !!accountingMigration && !accountingMigration.accountsMappedAt;

    const isMappingFinalized =
        !!accountingMigration?.accountsMappingFinalizedAt;

    useEffect(() => {
        if (!isAccountsMappingRunning) {
            refetchAccounts();
        }
    }, [refetchAccounts, isAccountsMappingRunning]);

    const handleMappingFinalized = useNavigateAfterMappingFinalized({
        nextTaskId,
    });

    const acceptMapping = useCallback(() => {
        finalizeMutation.mutate(
            {},
            {
                onSuccess: handleMappingFinalized,
            },
        );
    }, [finalizeMutation, handleMappingFinalized]);

    const confirmRefinalization = useCallback(async () => {
        await finalizeMutation.mutateAsync({ isRefinalization: true });
        setIsRefinalizeModalOpen(false);
        handleMappingFinalized();
    }, [finalizeMutation, handleMappingFinalized]);

    const accountCounts = useMemo(
        () =>
            countImportedAccountMappingResolutions({
                accounts: accountsData ?? [],
            }),
        [accountsData],
    );

    const [searchQuery, setSearchQuery] = useState<string | undefined>();

    const filteredAccounts = useMemo(() => {
        if (!accountsData) {
            return undefined;
        }
        if (!searchQuery) {
            return accountsData;
        }

        const query = searchQuery.toLowerCase();

        return accountsData.filter(
            (account) =>
                account.name.toLowerCase().includes(query) ||
                account.code?.toLowerCase().includes(query) ||
                account.providerType?.toLowerCase().includes(query),
        );
    }, [accountsData, searchQuery]);

    if (!filteredAccounts || !workspaceId) {
        return null;
    }

    const acceptActionLabel = isMappingFinalized
        ? "Update types"
        : "Accept types";

    return (
        <div className={styles.container}>
            <div className={styles.accountCounts}>
                <span>Imported accounts: {accountsData?.length ?? 0}</span>
                <span className={styles.separator}>·</span>
                <span>Missing type: {accountCounts.notMapped}</span>
                {accountCounts.connectInTasks > 0 && (
                    <>
                        <span className={styles.separator}>·</span>
                        <span>
                            Connect in tasks: {accountCounts.connectInTasks}
                        </span>
                    </>
                )}
            </div>

            <div className={styles.searchRow}>
                <SearchFilter
                    filter={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search..."
                    minLength={1}
                />
            </div>

            <MapImportedAccountsSpreadsheet
                accounts={filteredAccounts}
                accountGroups={accountGroups}
                entity={entity}
                workspaceId={workspaceId}
                entityId={entity.id}
                isAccountsMappingRunning={isAccountsMappingRunning}
                isTypeReview
                providerLabel={provider.label}
                providerHeader={
                    <ImportedAccountProviderHeader provider={provider} />
                }
                className={styles.spreadsheet}
            />
            <CommandIsland sticky>
                <span className={styles.commandIslandSpacer} />
                {accountingMigration && (
                    <ButtonWithLoader
                        loading={
                            finalizeMutation.isPending ||
                            finalizeMutation.isSuccess
                        }
                        variant="primary"
                        size="md"
                        hotKey="mod+enter"
                        hotKeyDisplayPosition="inline"
                        hotKeyOptions={{
                            description: acceptActionLabel,
                        }}
                        onClick={
                            isMappingFinalized
                                ? () => setIsRefinalizeModalOpen(true)
                                : acceptMapping
                        }
                        disabled={accountCounts.notMapped > 0}
                        tooltip={
                            accountCounts.notMapped > 0
                                ? "All imported accounts must have a type first"
                                : undefined
                        }
                    >
                        {acceptActionLabel}
                    </ButtonWithLoader>
                )}
            </CommandIsland>
            <RefinalizeMappingConfirmationModal
                show={isRefinalizeModalOpen}
                onConfirm={confirmRefinalization}
                onClose={() => setIsRefinalizeModalOpen(false)}
            />
        </div>
    );
};
